import { VoterDataExportHoldReason, VoterDataExportState, assertNever } from '@signalvote/shared';
import { cn } from '@/lib/utils';

type ExportTone = 'muted' | 'progress' | 'warning' | 'held';

interface ExportStateCopy {
  label: string;
  hint?: string;
  tone: ExportTone;
}

function exportStateCopy(state: VoterDataExportState): ExportStateCopy {
  switch (state) {
    case VoterDataExportState.NotStarted:
      return { label: 'Export not started', tone: 'muted' };
    case VoterDataExportState.Starting:
      return {
        label: 'Starting with L2',
        // The start may have reached L2 without its job id being saved. It is never retried, so a
        // retry can't buy the same list twice; a person has to look.
        hint: 'Never confirmed. Check L2 for this export before doing anything else.',
        tone: 'warning',
      };
    case VoterDataExportState.Exporting:
      return { label: 'Exporting, waiting on L2', tone: 'progress' };
    case VoterDataExportState.Downloading:
      return { label: 'Downloading the file', tone: 'progress' };
    case VoterDataExportState.Held:
      return { label: 'Held for review', tone: 'held' };
    default:
      return assertNever(state);
  }
}

function holdReasonLabel(reason: VoterDataExportHoldReason): string {
  switch (reason) {
    case VoterDataExportHoldReason.StartRefused:
      return 'L2 refused to start the export';
    case VoterDataExportHoldReason.JobError:
      return 'L2 reported the export failed';
    case VoterDataExportHoldReason.TimedOut:
      return 'L2 took too long to finish the export';
    case VoterDataExportHoldReason.DownloadFailed:
      return 'The finished file couldn’t be downloaded';
    case VoterDataExportHoldReason.DeliveredCountOutsideAllowance:
      return 'Records delivered are outside the allowance of the quote';
    case VoterDataExportHoldReason.MissingColumns:
      return 'The file is missing a required column';
    case VoterDataExportHoldReason.NoValidRecipients:
      return 'No valid cell phones for the text send it was bought with';
    case VoterDataExportHoldReason.Household:
      return 'One per household, which L2’s export can’t produce';
    case VoterDataExportHoldReason.TooLargeForAutomaticExport:
      return 'Too many voters to export automatically; export it from L2 by hand';
    default:
      return assertNever(reason);
  }
}

const TONE_CLASS: Record<ExportTone, string> = {
  muted: 'text-muted-foreground',
  progress: 'text-foreground',
  warning: 'text-warning',
  held: 'text-destructive',
};

interface VoterDataExportStatusProps {
  state: VoterDataExportState;
  holdReason: VoterDataExportHoldReason | null;
  holdDetail: string | null;
  className?: string;
}

/**
 * How far a paid Voter data order's automatic L2 export got, for a platform admin. A held export
 * names its reason and the server's plain-words detail. Hand fulfil stays open in every state.
 */
export function VoterDataExportStatus({ state, holdReason, holdDetail, className }: VoterDataExportStatusProps) {
  const copy = exportStateCopy(state);
  const held = state === VoterDataExportState.Held;
  return (
    <div data-testid="voter-data-export-status" data-tone={copy.tone} className={cn('space-y-0.5 text-sm', className)}>
      <p className={cn('font-medium', TONE_CLASS[copy.tone])}>{copy.label}</p>
      {copy.hint && <p className="text-xs text-warning">{copy.hint}</p>}
      {held && holdReason && (
        <p data-testid="voter-data-export-hold-reason" className="text-xs text-foreground">
          {holdReasonLabel(holdReason)}
        </p>
      )}
      {held && holdDetail && <p className="text-xs text-muted-foreground">{holdDetail}</p>}
    </div>
  );
}
